import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";
import {
  $createParagraphNode,
  $getAdjacentNode,
  $getNodeByKey,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isRootNode,
  $parseSerializedNode,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_LOW,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_UP_COMMAND,
  type LexicalNode,
} from "lexical";
import { useEffect } from "react";
import {
  $createDynamicContentNode,
  $isDynamicContentNode,
  DynamicContentNode,
} from "../../../../nodes/dynamic-content/dynamic-content-node";
import { DynamicContentToolbarNode } from "../../../../nodes/dynamic-content/dynamic-content-toolbar-node";
import {
  $createDynamicContentToolbarNode,
  $isDynamicContentToolbarNode,
} from "../../../../nodes/dynamic-content/helpers";
import { exportNodeToJSON } from "../../../../nodes/export-node-to-json";
import { $isPaperColorNode } from "../../../../nodes/paper-color-node";
import {
  CLONE_DYNAMIC_CONTENT_COMMAND,
  EDIT_DYNAMIC_CONTENT_COMMAND,
  INSERT_DYNAMIC_CONTENT_COMMAND,
  SET_DYNAMIC_CONTENT_OPEN_COMMAND,
} from "../../../../types/commands";
import { $insertNode } from "../../../../utils/insert-node";

export function useDynamicContentButtonCommands(error?: string): void {
  const [editor] = useLexicalComposerContext();

  useEffect(
    () =>
      editor.registerNodeTransform(DynamicContentToolbarNode, (node) => {
        const currentError = node.getError();
        if (currentError !== error) {
          node.setError(error);
        }
      }),
    [editor, error],
  );

  useEffect(
    () =>
      mergeRegister(
        // Registers a transform on DynamicContentNode to enforce structural rules
        editor.registerNodeTransform(DynamicContentNode, (node) => {
          if (!$isDynamicContentNode(node)) return;

          const children = node.getChildren();

          // Check if the only child is a DynamicContentToolbarNode
          const onlyToolbar = children.length === 1 && $isDynamicContentToolbarNode(children[0]);

          // If the node only contains the toolbar, add a paragraph below it
          if (onlyToolbar) {
            const paragraph = $createParagraphNode();
            node.append(paragraph);
          }

          // If toolbar is missing, insert it at the beginning
          if (!children.find($isDynamicContentToolbarNode)) {
            const toolbar = $createDynamicContentToolbarNode(
              editor._headless,
              node.getSegmentId(),
              error,
            );
            node.splice(0, 0, [toolbar]);
          }
        }),

        // Handles pressing the up arrow inside a DynamicContentNode
        editor.registerCommand(
          KEY_ARROW_UP_COMMAND,
          (event) => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
              return false;
            }

            const anchorNode = selection.anchor.getNode();
            const dynamicNode = anchorNode.getParents().find($isDynamicContentNode);
            if (!dynamicNode) {
              return false;
            }

            const adjacentNode = $getAdjacentNode(selection.focus, true); // Get node before the selection
            const isAtStart = !adjacentNode || $isDynamicContentToolbarNode(adjacentNode);
            const sibling = dynamicNode.getPreviousSibling();

            if (!isAtStart) {
              return false;
            }

            // If there's no sibling before or it's a special type, insert a paragraph
            if (!sibling || $isPaperColorNode(sibling)) {
              const newParagraph = $createParagraphNode();
              dynamicNode.insertBefore(newParagraph);
              newParagraph.select();

              event.preventDefault();
              return true;
            }

            // Otherwise, move selection to the previous toolbar
            if ($isDynamicContentToolbarNode(adjacentNode)) {
              adjacentNode.selectPrevious();
              return true;
            }

            return false;
          },
          COMMAND_PRIORITY_LOW,
        ),

        // Handles pressing the down arrow at the end of a DynamicContentNode
        editor.registerCommand(
          KEY_ARROW_DOWN_COMMAND,
          (event) => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
              return false;
            }

            const anchorNode = selection.anchor.getNode();
            const dynamicNode = anchorNode.getParents().find($isDynamicContentNode);
            if (!dynamicNode) return false;

            const isAtEnd = $getAdjacentNode(selection.focus, false) === null;
            const noSiblingAfter = dynamicNode.getNextSibling() === null;

            // If at the end and no next sibling exists, insert a paragraph below
            if (isAtEnd && noSiblingAfter) {
              const newParagraph = $createParagraphNode();
              dynamicNode.insertAfter(newParagraph);
              newParagraph.select();

              event.preventDefault();
              return true;
            }

            return false;
          },
          COMMAND_PRIORITY_LOW,
        ),

        // Inserts a new DynamicContentNode into the document
        editor.registerCommand(
          INSERT_DYNAMIC_CONTENT_COMMAND,
          ({ segment_id }) => {
            editor.update(() => {
              const selection = $getSelection();
              const dynamic = $createDynamicContentNode(segment_id);
              const toolbar = $createDynamicContentToolbarNode(editor._headless, segment_id, error);
              dynamic.append(toolbar);

              let movedSelection = false;

              if ($isRangeSelection(selection) && !selection.isCollapsed()) {
                const selectedNodes = selection.getNodes();
                const topLevelBlocks = new Set<LexicalNode>();
                for (const node of selectedNodes) {
                  if ($isElementNode(node) && $isRootNode(node.getParent())) {
                    topLevelBlocks.add(node);
                  } else {
                    const topLevel = node.getTopLevelElementOrThrow();
                    topLevelBlocks.add(topLevel);
                  }
                }

                if (topLevelBlocks.size > 0) {
                  movedSelection = true;
                  for (const block of topLevelBlocks) {
                    block.remove();
                    dynamic.append(block);
                  }
                }
              }

              $insertNode(dynamic);

              // If no content was moved, insert empty paragraph
              if (!movedSelection) {
                const paragraph = $createParagraphNode();
                dynamic.append(paragraph);
                paragraph.select();
              }
            });

            return true;
          },
          COMMAND_PRIORITY_EDITOR,
        ),

        // Updates an existing DynamicContentNode's segment ID
        editor.registerCommand(
          EDIT_DYNAMIC_CONTENT_COMMAND,
          ({ key, segment_id }) => {
            editor.update(() => {
              const node = $getNodeByKey(key);
              if (!$isDynamicContentNode(node)) {
                return false;
              }

              node.setSegmentId(segment_id);

              // Also update the segment ID of the toolbar, if present
              const childNode = node.getChildren().find($isDynamicContentToolbarNode);
              childNode?.setSegmentId(segment_id);
            });

            return true;
          },
          COMMAND_PRIORITY_EDITOR,
        ),

        // Clones an existing DynamicContentNode
        editor.registerCommand(
          CLONE_DYNAMIC_CONTENT_COMMAND,
          (key) => {
            editor.update(() => {
              const node = $getNodeByKey(key);
              if (!$isDynamicContentNode(node)) {
                return false;
              }

              const data = exportNodeToJSON(node);
              const newNode = $parseSerializedNode(data);

              // Insert cloned node after original
              node.insertAfter(newNode);
            });

            return true;
          },
          COMMAND_PRIORITY_EDITOR,
        ),

        // Expands or collapses a DynamicContentNode
        editor.registerCommand(
          SET_DYNAMIC_CONTENT_OPEN_COMMAND,
          ({ key, open }) => {
            editor.update(() => {
              const node = $getNodeByKey(key);
              if (!$isDynamicContentNode(node)) {
                return false;
              }

              node.setOpen(open);

              // Sync open state with toolbar
              const child = node.getChildren().find($isDynamicContentToolbarNode);
              child?.setOpen(open);
            });

            return true;
          },
          COMMAND_PRIORITY_EDITOR,
        ),
      ),
    [editor, error],
  );
}
